import {
  createEntityAdapter,
  createSlice,
  type EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import {
  createRollup,
  fetchQuestion,
  updateQuestionOptionColor,
} from "./questions.thunk";
import type { Question } from "@/interfaces/questions";

const questionsAdalpter = createEntityAdapter<Question, string>({
  // @ts-ignore
  selectId: (question: Question) => question.questionKey,
});

interface QuestionsState {
  questions: ReturnType<typeof questionsAdalpter.getInitialState>;
  loading: boolean;
  isQuestionsFetchedInitially: boolean;
  error: string | null;
}

const initialState: QuestionsState = {
  questions: questionsAdalpter.getInitialState(),
  loading: false,
  isQuestionsFetchedInitially: false,
  error: null,
};

export const questionsSlice = createSlice({
  name: "questions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuestion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchQuestion.fulfilled,
        (state, action: PayloadAction<Question[] | any>) => {
          if (action.payload.error && action.payload.error?.length > 0) {
            return;
          }
          state.loading = false;
          state.error = null;
          state.isQuestionsFetchedInitially = true;
          questionsAdalpter.setAll(state.questions, action.payload);
        }
      )
      .addCase(fetchQuestion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load questions";
      })
      .addCase(
        updateQuestionOptionColor.fulfilled,
        (
          state,
          action: PayloadAction<{ entityId: number; color: string; entityType: "QUESTION_OPTION" | "ROLLUP" } | null>
        ) => {
          if (action.payload) {
            const { entityId, color, entityType } = action.payload;
            const questionIds = Object.keys(state.questions.entities);
            for (const questionId of questionIds) {
              const question = state.questions.entities[questionId];
              if (entityType === "QUESTION_OPTION" && question && question.options) {
                const optionIndex = question.options.findIndex(
                  (opt) => opt.optionId === entityId
                );
                if (optionIndex !== -1) {
                  question.options[optionIndex].color = color;
                  break;
                }
              } else if (entityType === "ROLLUP" && question && question.rollups) {
                const rollupIndex = question.rollups.findIndex(
                  (r: any) => r.rollupId === entityId
                );
                if (rollupIndex !== -1) {
                  question.rollups[rollupIndex].rollupColor = color;
                  break;
                }
              }
            }
          }
        }
      )
      .addCase(createRollup.fulfilled, (state, action) => {});
  },
});

export type QuestionsAdapter = EntityState<Question, string>;

export default questionsSlice.reducer;
